"use client";

import React, { useState } from "react";
import Image from "next/image";
import { 
  Maximize2, 
  X, 
  Sparkles, 
  Layers, 
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Activity,
  ShieldCheck,
  Bot,
  Building,
  KeyRound,
  CheckCircle2,
  TrendingUp,
  UserCheck,
  Eye,
  Info
} from "lucide-react";

export function PlatformGallerySection() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [selectedImage, setSelectedImage] = useState<{ 
    src: string; 
    title: string; 
    category: string; 
    tag: string;
    description: string;
    useCase: string;
    whoUses: string;
    keyMetric: string;
  } | null>(null);

  const screens = [
    {
      src: "/ss/07_athletic_testing_radar.png",
      title: "6-Axis Biometric Radar & PHV Biological Offset",
      category: "science",
      tag: "Biometric Lab",
      description: "Automated Eurofit battery scoring across 6 physiological stations. Instantly isolates biological maturation from calendar age to discover latent athletic talent.",
      useCase: "PE teacher records shuttle run & broad jump; system plots 6-axis radar and flags student as potential national track prospect.",
      whoUses: "PE Directors, Athletic Coaches, School Scouts",
      keyMetric: "98.4th National Percentile Benchmark",
    },
    {
      src: "/ss/08_athletic_testing_heatmap.png",
      title: "School-Wide Athletic Talent Heatmap Matrix",
      category: "science",
      tag: "Talent Scouting",
      description: "Cohort-level visual intelligence displaying stamina, power, and sprint distributions across classes (Grade 5 to Grade 12) with one-click filtering.",
      useCase: "Instantly build the school football or track team squad based on real physical fitness stats rather than teacher subjective preference.",
      whoUses: "Head of Sports, Principal",
      keyMetric: "Filters 1,200+ Students in 1 Click",
    },
    {
      src: "/ss/06_athletic_testing_results.png",
      title: "High-Speed Class Intake Keyboard Grid",
      category: "science",
      tag: "15-Min Class Intake",
      description: "Keyboard-optimized data entry grid with arrow-key navigation that allows coaches to enter an entire class's Eurofit scores in under 15 minutes.",
      useCase: "Enter 40 students' sprint, flexibility, and grip-strength scores in minutes with automatic validation and error detection.",
      whoUses: "Physical Education Teachers",
      keyMetric: "4x Faster Than Paper Clipboards",
    },
    {
      src: "/ss/09_athletic_testing_medical.png",
      title: "Clinical Medical Safety Gate (AAP PPE)",
      category: "science",
      tag: "Clinical Safety",
      description: "American Academy of Pediatrics (AAP) digital medical clearance. Evaluates cardiac flags, asthma triggers, and concussions before physical exertion.",
      useCase: "Automatically prevents students with active asthma attacks or concussion recovery from participating in maximal aerobic testing.",
      whoUses: "School Infirmary, Campus Nurse, PE Coaches",
      keyMetric: "100% Legal & Clinical Protection",
    },
    {
      src: "/ss/15_principal_portal_hub.png",
      title: "Principal Macro Health & Sports Command Hub",
      category: "executive",
      tag: "Executive Command",
      description: "Executive high-level dashboard showing total campus fitness indices, house competition standing, facility revenue yield, and injury audit logs.",
      useCase: "School board review and institutional accreditation: showcase verifiable physical health improvements across the student body.",
      whoUses: "Principals, Directors, Board of Trustees",
      keyMetric: "Complete School Sports Transparency",
    },
    {
      src: "/ss/02_admin_dashboard.png",
      title: "Sports Department Operations Center",
      category: "executive",
      tag: "Operations Hub",
      description: "Real-time command center overseeing daily coaching schedules, ground maintenance, inventory checkouts, and upcoming tournament fixtures.",
      useCase: "Manage 8 sports coaches, 4 playing fields, and 500 sports assets from a single unified operational screen.",
      whoUses: "Sports Administrator, Department Head",
      keyMetric: "Single-Pane-of-Glass Campus Control",
    },
    {
      src: "/ss/18_annual_meet_management.png",
      title: "Annual Sports Day & Track Heats Orchestrator",
      category: "executive",
      tag: "Sports Day Ops",
      description: "End-to-end management for Annual Sports Days. Automates track heat lane assignments, bib numbers, photo-finish scoring, and house points.",
      useCase: "Execute an 800-student Annual Sports Meet with zero delay, instant scoreboard updates, and automated medal point tallying.",
      whoUses: "Sports Day Committee, House Masters",
      keyMetric: "Saves 20+ Hours of Manual Scheduling",
    },
    {
      src: "/ss/19_tournaments_fixtures.png",
      title: "Tournament Brackets & Live Match Scoring",
      category: "executive",
      tag: "Competition Engine",
      description: "Automated single-elimination, double-elimination, and round-robin bracket generator with live point updates and verified digital certificates.",
      useCase: "Organize inter-school football, basketball, and cricket leagues with instant score updates sent directly to parents' phones.",
      whoUses: "Tournament Directors, Match Referees",
      keyMetric: "Automated 64-Team Knockout Brackets",
    },
    {
      src: "/ss/10_student_portal_home.png",
      title: "Student Athlete Command & XP Gamification",
      category: "student",
      tag: "Student Athlete",
      description: "Mobile-first student athlete home showing physical fitness level, personal bests, house tournament schedule, and training streak badges.",
      useCase: "Empowers students to track their athletic progress, earn badges, and stay motivated to maintain physical fitness beyond PE class.",
      whoUses: "Students (Ages 10–18), Student Captains",
      keyMetric: "Boosts Student Sports Engagement +40%",
    },
    {
      src: "/ss/11_student_portal_aicoach.png",
      title: "24/7 AI Athletic Coach & Interactive Training",
      category: "student",
      tag: "AI Sports Mentorship",
      description: "Interactive AI sports mentor trained on sports science. Analyzes student radar scores, designs daily workout routines, and provides recovery tips.",
      useCase: "Student asks: 'How do I improve my sprint start latency before next week's heats?' — AI gives tailored drills and warm-up regimen.",
      whoUses: "Students, Budding Athletes, Track Squads",
      keyMetric: "1-on-1 Personalized Athletic Guidance",
    },
    {
      src: "/ss/12_student_portal_leaderboard.png",
      title: "Inter-House & Regional Talent Leaderboards",
      category: "student",
      tag: "Inter-House Pride",
      description: "Live, transparent rankings by age category, sport, and house. Rewards both top elite performers and students showing highest month-on-month improvement.",
      useCase: "Fosters healthy competitive spirit during inter-house sports competitions while celebrating every student's individual progress.",
      whoUses: "Students, House Masters, Coaches",
      keyMetric: "Celebrates Growth & Personal Bests",
    },
    {
      src: "/ss/13_parent_portal_report.png",
      title: "Parent Digital Sports Report Card & Passport",
      category: "student",
      tag: "Parent Transparency",
      description: "Comprehensive mobile sports passport for parents tracking VO2 max, motor agility, growth velocity, and medical safety clearances under DPDP Act 2023.",
      useCase: "Parents view child's physical development alongside academic marks, discovering their athletic strengths early without private coaching expenses.",
      whoUses: "Parents & Guardians",
      keyMetric: "100% DPDP Act Compliant Parent Access",
    },
    {
      src: "/ss/16_facilities_management.png",
      title: "Smart Facility Capacity & Playing Units",
      category: "facilities",
      tag: "Campus Assets",
      description: "Real-time layout of all sports courts, synthetic turfs, pools, and tracks. Configures daytime school hours vs evening revenue monetization slots.",
      useCase: "Lock synthetic turf for school teams 8 AM–3:30 PM; automatically unlock for verified community rental 4 PM–9 PM.",
      whoUses: "Facility Managers, Bursar, Grounds Head",
      keyMetric: "Zero Scheduling Overlaps Guaranteed",
    },
    {
      src: "/ss/20_explore_marketplace.png",
      title: "Community Sports Facility Marketplace",
      category: "facilities",
      tag: "Passive Campus Revenue",
      description: "Public-facing booking portal allowing verified community sports lovers and corporate leagues to rent unused school sports courts after-hours.",
      useCase: "Neighborhood cricket club books school synthetic turf on Sunday morning via UPI; generates ₹8,000 direct revenue for school trust.",
      whoUses: "Community Athletes, Sports Academies, Public",
      keyMetric: "Generates ₹3L–₹8L Monthly Campus Revenue",
    },
    {
      src: "/ss/17_inventory_qr_management.png",
      title: "QR Sports Inventory & Custody Lifecycle",
      category: "facilities",
      tag: "Equipment Custody",
      description: "Track footballs, rackets, timing gates, and gym equipment with durable QR tags. Complete checkout and return audit log in 3 seconds.",
      useCase: "Student captain scans QR on 10 basketballs before practice; system logs check-out and alerts teacher if items are not returned by 5 PM.",
      whoUses: "PE Teachers, Sports Attendants, Equipment Store",
      keyMetric: "Zero Lost or Stolen Sports Gear",
    },
    {
      src: "/ss/21_guard_scanner.png",
      title: "Cryptographic HMAC Guard Gate Pass Scanner",
      category: "security",
      tag: "Perimeter Security",
      description: "Guard mobile camera scanner that validates single-use HMAC-SHA256 encrypted QR passes offline in 0.2 seconds. Prevents unauthorized campus entry.",
      useCase: "Community player arrives at school gate at 6 PM; guard scans phone QR code; gate turns green with visitor photo and time-locked clearance.",
      whoUses: "Campus Security Guards, Gate Staff",
      keyMetric: "Zero-Trust School Perimeter Defense",
    }
  ];

  const categories = [
    { id: "all", label: "All Interfaces (16)", icon: Layers },
    { id: "science", label: "Athletic Science & Radar", icon: Activity },
    { id: "executive", label: "Principal & Admin", icon: Building },
    { id: "student", label: "Student & AI Coach", icon: Bot },
    { id: "facilities", label: "Facilities & Marketplace", icon: TrendingUp },
    { id: "security", label: "Security & Guard", icon: KeyRound }
  ];

  const filteredScreens = activeCategory === "all"
    ? screens
    : screens.filter(s => s.category === activeCategory);

  return (
    <section id="gallery" className="py-24 bg-[#070A11] relative border-t border-white/5">
      {/* Background radial glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/5 blur-[160px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-white/10 text-xs font-mono text-cyan-400 uppercase tracking-widest">
            <Layers className="w-3.5 h-3.5" />
            REAL PRODUCTION PLATFORM
          </div>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
            Explore the Live Interface.
          </h2>
          <p className="text-slate-400 text-base sm:text-lg">
            High-density biometric radars, automated tournament brackets, after-hours facility slots, and cryptographic mobile security passes.
          </p>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center justify-center gap-2 pt-4">
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-xs font-mono transition-all flex items-center gap-1.5 ${
                    activeCategory === cat.id
                      ? "bg-[#9EFFBF] text-[#070A11] font-bold shadow-[0_0_20px_rgba(158,255,191,0.3)]"
                      : "bg-slate-900/90 text-slate-300 hover:text-white border border-white/10"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feature Explainer Notice */}
        <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-slate-300">
          <div className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#9EFFBF] animate-pulse" />
            <span>
              <strong>PRO-LEVEL WORKFLOWS:</strong> Click any interface below to inspect its practical school use cases, target users, and key operational metrics.
            </span>
          </div>
          <span className="text-[#9EFFBF] text-[11px] font-bold uppercase tracking-wider shrink-0">
            16 PRODUCTION SCREENS READY
          </span>
        </div>

        {/* Screens Grid with Enhanced Explainer Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredScreens.map((screen, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedImage(screen)}
              className="group cursor-pointer rounded-2xl bg-[#0F172A] border border-white/10 overflow-hidden shadow-xl hover:border-[#9EFFBF]/60 hover:shadow-[0_10px_30px_rgba(158,255,191,0.15)] transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
            >
              {/* Image Preview */}
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-950">
                <Image
                  src={screen.src}
                  alt={screen.title}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="px-3.5 py-1.5 rounded-lg bg-[#9EFFBF] text-[#070A11] font-outfit font-bold text-xs flex items-center gap-1.5 shadow-lg">
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>Inspect Module</span>
                  </div>
                </div>
                {/* Category Pill on Image */}
                <div className="absolute top-2.5 left-2.5 bg-black/80 backdrop-blur-sm border border-white/10 px-2 py-0.5 rounded text-[10px] font-mono font-bold text-[#9EFFBF] uppercase">
                  {screen.tag}
                </div>
              </div>

              {/* Information & Layman Explanation */}
              <div className="p-4 space-y-2.5 bg-[#0F172A] flex-1 flex flex-col justify-between">
                <div>
                  <h4 className="font-outfit font-bold text-sm text-white group-hover:text-[#9EFFBF] transition-colors leading-snug">
                    {screen.title}
                  </h4>
                  <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-2 leading-relaxed">
                    {screen.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-white/5 space-y-1 text-[10px] font-mono">
                  <div className="flex items-center justify-between text-slate-400">
                    <span>Key Metric:</span>
                    <span className="text-[#9EFFBF] font-bold truncate max-w-[150px]">{screen.keyMetric}</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-500">
                    <span>Users:</span>
                    <span className="text-slate-300 truncate max-w-[150px]">{screen.whoUses}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal Lightbox with Full Explainer Walkthrough */}
        {selectedImage && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6">
            <div 
              className="fixed inset-0 bg-black/90 backdrop-blur-md"
              onClick={() => setSelectedImage(null)}
            />

            <div className="relative w-full max-w-5xl bg-[#0F172A] border border-white/20 rounded-3xl overflow-hidden shadow-2xl z-10 space-y-4 p-5 sm:p-6 text-white max-h-[95vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-white/10 pb-3 shrink-0">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-[#9EFFBF] font-bold uppercase tracking-wider">
                      MODULE PREVIEW // {selectedImage.tag}
                    </span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-white/10">
                      LIVE PRODUCTION INTERFACE
                    </span>
                  </div>
                  <h3 className="font-outfit text-xl sm:text-2xl font-bold text-white">
                    {selectedImage.title}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Large Image View */}
              <div className="relative flex-1 min-h-[260px] sm:min-h-[460px] w-full rounded-2xl overflow-hidden border border-white/10 bg-black">
                <Image
                  src={selectedImage.src}
                  alt={selectedImage.title}
                  fill
                  className="object-contain"
                />
              </div>

              {/* Rich Explainer Drawer inside Lightbox */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-900/90 border border-white/10 p-4 rounded-xl text-xs font-mono shrink-0">
                <div className="space-y-1">
                  <span className="text-[10px] text-[#9EFFBF] font-bold uppercase block">
                    1. PURPOSE &amp; HOW IT WORKS:
                  </span>
                  <p className="text-slate-300 leading-relaxed text-[11px]">
                    {selectedImage.description}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] text-amber-400 font-bold uppercase block">
                    2. REAL CAMPUS USE CASE:
                  </span>
                  <p className="text-slate-300 leading-relaxed text-[11px]">
                    {selectedImage.useCase}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] text-cyan-400 font-bold uppercase block">
                    3. STAKEHOLDERS &amp; VALUE:
                  </span>
                  <p className="text-slate-300 text-[11px]">
                    <strong>Target Users:</strong> {selectedImage.whoUses}
                  </p>
                  <p className="text-slate-300 text-[11px] mt-1">
                    <strong>Impact:</strong> {selectedImage.keyMetric}
                  </p>
                </div>
              </div>

              {/* Lightbox Footer */}
              <div className="flex items-center justify-between text-xs font-mono text-slate-400 pt-1 shrink-0">
                <span>Enterprise Responsive Layout (Supports Desktop, iPad &amp; Mobile Web)</span>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-[#9EFFBF] font-bold hover:underline"
                >
                  Close Lightbox
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

